// Today — home dashboard. Hero + Quick chat + 6 tiles aggregating
// the other sections.
//
// Phase 1 wires the tiles to real hooks. Skipped for now (parity but
// no data source yet):
//   • Weather card on hero
//   • "Just imagined" image-gen rollup
//   • "Latest thread" — could pull from api.sessionList, deferring
//     until we have a session-history hook
//
// Tile clicks navigate via `setTab` from the parent shell.

import { useEffect, useMemo, useState } from "preact/hooks";
import "../../styles/today.css";

import { Icon } from "../../icons";
import type { Me } from "../../lib/use-me";
import { api, type GalleryItem, type Session } from "../../lib/rpc";
import { useTodos } from "../../lib/use-todos";
import { useShopping } from "../../lib/use-shopping";
import { useMealPlan } from "../../lib/use-mealplan";
import { useCalendar } from "../../lib/use-calendar";
import { useCalendars } from "../../lib/use-calendars";
import { useNotes } from "../../lib/use-notes";
import { useWeather } from "../../lib/use-weather";
import { colorFor, dateKey, eventDayHours, fmtTime } from "../../lib/calendar-utils";
import { QuickChat } from "./quick-chat";
import { UseItUpCard } from "./use-it-up-card";

interface TodayViewProps {
  me: Me | null;
  setTab: (id: string) => void;
  setFoodInitialMode?: (mode: "recipes") => void;
  setFoodInitialRecipeId?: (id: string) => void;
  notify: (msg: string) => void;
  setPendingChatMessage: (msg: string | null) => void;
  kioskProfile?: string | null;
}

export function TodayView({ me, setTab, setFoodInitialMode, setFoodInitialRecipeId, notify, setPendingChatMessage, kioskProfile }: TodayViewProps) {
  const isKiosk = kioskProfile === "kitchen";
  const todos = useTodos();
  const shopping = useShopping();
  const mealplan = useMealPlan();
  const calendar = useCalendar();
  const cals = useCalendars();
  const notes = useNotes();
  const wx = useWeather();

  // Lightweight loads — fetch directly via api so we don't mount the
  // full useChat / useImageGen hooks just for a snapshot tile (those
  // open SSE channels and start polling).
  const [recentThread, setRecentThread] = useState<Session | null>(null);
  const [recentImages, setRecentImages] = useState<GalleryItem[]>([]);
  useEffect(() => {
    let cancelled = false;
    api.sessionList()
      .then((list) => {
        if (cancelled) return;
        const chats = list.filter((s) => s.kind === "chat");
        chats.sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
        setRecentThread(chats[0] || null);
      })
      .catch(() => {});
    api.galleryList({ limit: 4 })
      .then((g) => { if (!cancelled) setRecentImages(g); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const now = useMemo(() => new Date(), []);
  const hour = now.getHours();
  const greet =
    hour < 5  ? "Late night" :
    hour < 12 ? "Good morning" :
    hour < 17 ? "Good afternoon" :
                "Good evening";
  const dateLabel = now.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" });
  const todayKey = dateKey(now);
  const initial = (me?.username || "y").charAt(0).toUpperCase();

  // Tonight's dinner — meal plan entry for today's dinner slot. Free-
  // form value or recipe name; both render the same way here.
  const dinnerEntry = mealplan.cell(todayKey, "dinner");
  const tonight = dinnerEntry?.value || null;

  // Today's events — anything intersecting today (timed events that
  // span midnight, multi-day all-day events). Sorted by start, capped
  // at 3 for the tile.
  const todayEvents = useMemo(() => {
    return calendar.events
      .filter((e) => {
        if (e.allDay) {
          // All-day "dtend" is exclusive — include events whose visible
          // span covers today.
          const start = e.dtstart.slice(0, 10);
          const end = e.dtend.slice(0, 10);
          return start <= todayKey && todayKey < end;
        }
        return eventDayHours(e, now) !== null;
      })
      .sort((a, b) => a.dtstart.localeCompare(b.dtstart))
      .slice(0, 3);
  }, [calendar.events, todayKey, now]);

  const openTodos = useMemo(() => todos.items.filter((t) => !t.done), [todos.items]);
  const openShop = useMemo(() => shopping.items.filter((i) => !i.purchased), [shopping.items]);
  const recentNotes = notes.notes.slice(0, 3);

  const onQuickChatSend = (text: string) => {
    setPendingChatMessage(text);
    setTab("chat");
  };

  return (
    <div className="today-shell">
      <section className="today-hero">
        <div className="today-hero-text">
          <div className="eyebrow today-hero-date">{dateLabel}</div>
          <h1 className="today-hero-greet">
            {greet}, <span className="today-hero-initial">{initial}.</span>
          </h1>
          <p className="today-hero-sub">
            {tonight
              ? <>Tonight's dinner is <strong>{tonight}</strong>. A few small things in your list — pick whichever feels good.</>
              : <>Nothing planned for dinner yet. The week's open — pick whichever direction feels good.</>}
          </p>
        </div>
        {wx.weather && (
          <div className="today-weather">
            <div className="today-weather-icon">
              <Icon name="sun" size={22} />
            </div>
            <div>
              <div className="today-weather-temp">{wx.weather.temp}°</div>
              <div className="today-weather-meta">
                {wx.weather.label} · H {wx.weather.high}° L {wx.weather.low}°
              </div>
            </div>
          </div>
        )}
      </section>

      <QuickChat onSend={onQuickChatSend} notify={notify} />

      <div className="today-grid">
        <Tile
          title="Up next today"
          subtitle={`${todayEvents.length} ${todayEvents.length === 1 ? "event" : "events"}`}
          onClick={() => setTab("calendar")}
          accent
        >
          {todayEvents.length === 0 ? (
            <div className="today-empty">Nothing on the calendar.</div>
          ) : (
            <div className="today-list">
              {todayEvents.map((e) => {
                const span = e.allDay ? null : eventDayHours(e, now);
                return (
                  <div key={e.id} className="today-event-row">
                    <span
                      className="today-event-bar"
                      style={{ background: `var(--${colorFor(e, cals.calendars)})` }}
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className="today-event-title">{e.title}</div>
                      <div className="today-event-time">
                        {e.allDay ? "all day" : span ? `${fmtTime(span.start)} – ${fmtTime(span.end)}` : ""}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Tile>

        {!isKiosk && (
        <Tile
          title="To-do"
          subtitle={`${openTodos.length} waiting`}
          onClick={() => setTab("todo")}
        >
          {openTodos.length === 0 ? (
            <div className="today-empty">All clear. Take a breath.</div>
          ) : (
            <div className="today-list">
              {openTodos.slice(0, 4).map((t) => (
                <div key={t.id} className="today-todo-row">
                  <span className="cbx" />
                  <div className="today-todo-title">{t.title}</div>
                  {t.dueAt && (
                    <span className="today-due-chip">{fmtRelDue(t.dueAt)}</span>
                  )}
                </div>
              ))}
            </div>
          )}
        </Tile>
        )}

        <Tile
          title="Shopping"
          subtitle={shopping.activeList
            ? `${openShop.length} to grab · ${shopping.activeList.name}`
            : "—"}
          onClick={() => setTab("food")}
        >
          {openShop.length === 0 ? (
            <div className="today-empty">List is clean.</div>
          ) : (
            <div className="today-list">
              {openShop.slice(0, 4).map((i) => (
                <div key={i.id} className="today-shop-row">
                  <span className="today-shop-dot" />
                  <div className="today-shop-title">{i.text}</div>
                  {i.quantity && (
                    <div className="today-shop-qty">{i.quantity}</div>
                  )}
                </div>
              ))}
            </div>
          )}
        </Tile>

        <UseItUpCard
          setTab={setTab}
          setFoodInitialMode={(mode) => setFoodInitialMode?.(mode)}
          setFoodInitialRecipeId={(id) => setFoodInitialRecipeId?.(id)}
        />

        <Tile
          title="Tonight's dinner"
          subtitle={tonight ? "Dinner" : "Open"}
          onClick={() => setTab("food")}
        >
          <div className="today-meal-hero">
            {tonight || "Nothing planned"}
          </div>
          <div className="today-meal-meta">
            {tonight
              ? <>Ingredients live in your shopping list</>
              : <>Pick a recipe from the meal plan view</>}
          </div>
        </Tile>

        {!isKiosk && (
        <Tile
          title="Recent notes"
          subtitle={`Across ${notes.roots.length || 0} workspace${notes.roots.length === 1 ? "" : "s"}`}
          onClick={() => setTab("notes")}
        >
          {recentNotes.length === 0 ? (
            <div className="today-empty">No notes yet.</div>
          ) : (
            <div className="today-list">
              {recentNotes.map((n) => (
                <div key={n.path} className="today-note-row">
                  <span
                    className="today-note-dot"
                    style={{ background: notes.activeRoot?.color || "var(--accent)" }}
                  />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="today-note-title">{n.title}</div>
                    <div className="today-note-meta">
                      {notes.activeRoot?.label || "—"}
                      {n.mtimeMs ? ` · ${fmtRelTime(n.mtimeMs)}` : ""}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Tile>
        )}

        {!isKiosk && (
        <Tile
          title="Latest thread"
          subtitle={recentThread ? fmtRelTime(new Date(recentThread.updatedAt).getTime()) : "Ask anything"}
          onClick={() => setTab("chat")}
        >
          {recentThread ? (
            <>
              <div className="today-quote">
                "{recentThread.name || "Untitled conversation"}"
              </div>
              {recentThread.preview && (
                <div className="today-quote-sub today-quote-preview">
                  {recentThread.preview}
                </div>
              )}
            </>
          ) : (
            <>
              <div className="today-quote">
                "What would you like to talk through?"
              </div>
              <div className="today-quote-sub">
                Drop a thought into Quick chat above, or open the full thread.
              </div>
            </>
          )}
        </Tile>
        )}

        {!isKiosk && (
        <Tile
          title="Just imagined"
          subtitle={recentImages.length > 0
            ? `${recentImages.length} recent`
            : "Nothing yet"}
          onClick={() => setTab("images")}
          wide
        >
          {recentImages.length === 0 ? (
            <div className="today-empty">Generate one to fill this in.</div>
          ) : (
            <div className="today-images-grid">
              {recentImages.map((img) => (
                <div key={img.id} className="today-image">
                  <img
                    src={`/api/gallery-img/${img.id}`}
                    alt={img.prompt}
                    loading="lazy"
                  />
                </div>
              ))}
            </div>
          )}
          {recentImages[0]?.prompt && (
            <div className="today-images-caption">
              "{recentImages[0].prompt}"
            </div>
          )}
        </Tile>
        )}
      </div>
    </div>
  );
}

interface TileProps {
  title: string;
  subtitle: string;
  onClick: () => void;
  accent?: boolean;
  wide?: boolean;
  children: any;
}
function Tile({ title, subtitle, onClick, accent, wide, children }: TileProps) {
  return (
    <div
      className={`today-tile${accent ? " today-tile-accent" : ""}${wide ? " today-tile-wide" : ""}`}
      onClick={onClick}
    >
      <div className="today-tile-hd">
        <div>
          <h3>{title}</h3>
          <div className="today-tile-sub">{subtitle}</div>
        </div>
        <span className="today-tile-go" aria-hidden>
          <Icon name="arrow-right" size={16} />
        </span>
      </div>
      {children}
    </div>
  );
}

// Short relative timestamp for note + due-date rollups. Avoids
// re-importing formatRelative from notes/index.tsx — same shape, just
// scoped here so the Today view is self-contained.
function fmtRelTime(ms: number): string {
  if (!ms) return "";
  const diff = Math.max(0, Date.now() - ms);
  const sec = Math.floor(diff / 1000);
  if (sec < 60) return "now";
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h`;
  const day = Math.floor(hr / 24);
  if (day === 1) return "yesterday";
  if (day < 7) return `${day}d`;
  return `${Math.floor(day / 7)}w`;
}

function fmtRelDue(iso: string): string {
  const t = new Date(iso).getTime();
  if (isNaN(t)) return "";
  const diff = t - Date.now();
  const abs = Math.abs(diff);
  const hours = Math.floor(abs / 3_600_000);
  const days = Math.floor(hours / 24);
  if (diff < 0) {
    if (days >= 1) return `${days}d late`;
    if (hours >= 1) return `${hours}h late`;
    return "late";
  }
  if (days >= 1) return `${days}d`;
  if (hours >= 1) return `${hours}h`;
  const mins = Math.floor(abs / 60_000);
  return `${mins}m`;
}
